import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CourseProvider, ProgressProvider, useAuth } from '@context';
import { useCourseContext, useProgressContext } from '@context';
import { CourseDashboard } from '@components';
import { ROUTES, generatePath } from '@config/routes';
import { Container, Spinner } from 'react-bootstrap';
import { canViewCourse } from '@utils/permissions';

/**
 * Course Page
 * Shows course dashboard with progress
 * Optimized with lazy loading for large courses
 */
function CoursePageContent() {
  const { course, loading: courseLoading, loadSectionDetails } = useCourseContext();
  const { progress, loading: progressLoading } = useProgressContext();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { courseId } = useParams();

  const handleStartLearning = () => {
    navigate(generatePath(ROUTES.LEARNING_SESSION, { courseId }));
  };

  const handleStartQuestionPractice = () => {
    navigate(generatePath(ROUTES.QUESTION_PRACTICE, { courseId }));
  };

  const handleStartFlashcards = () => {
    navigate(generatePath(ROUTES.FLASHCARDS, { courseId }));
  };

  const handleStartReviewContent = () => {
    navigate(generatePath(ROUTES.REVIEW_CONTENT, { courseId }));
  };

  if (courseLoading || progressLoading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <CourseDashboard 
      course={course} 
      progress={progress}
      userId={user?.id}
      onStartLearning={handleStartLearning}
      onStartQuestionPractice={handleStartQuestionPractice}
      onStartFlashcards={handleStartFlashcards}
      onStartReviewContent={handleStartReviewContent}
      onLoadSection={loadSectionDetails}
    />
  );
}

export function CoursePage() {
  const { courseId } = useParams();
  const { user } = useAuth();

  if (!user) {
    return (
      <Container className="py-5">
        <div className="alert alert-warning">
          Please log in to access courses.
        </div>
      </Container>
    );
  }

  if (!canViewCourse(user, courseId)) {
    return (
      <Container className="py-5">
        <div className="alert alert-danger">
          Access denied: You do not have access to this course.
        </div>
      </Container>
    );
  }

  return (
    <CourseProvider courseId={courseId}>
      <ProgressProvider courseId={courseId}>
        <CoursePageContent />
      </ProgressProvider>
    </CourseProvider>
  );
}
